<template>
  <div class="cultural-fit-tab">
    <v-card class="mb-6">
      <v-card-title class="primary--text">
        <v-icon left color="primary">mdi-office-building</v-icon>
        Cultural Fit Analysis
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-sheet rounded class="metric-sheet pa-4" :style="kpiStyleByPercent(globalMatchScore)">
              <div class="caption grey--text">Cultural Match</div>
              <div class="text-h5 font-weight-bold">{{ formatScore(globalMatchScore) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="12" md="4">
            <v-sheet rounded class="metric-sheet pa-4" :style="kpiStyleByPercent(orderMatchScore)">
              <div class="caption grey--text">Ranking Match</div>
              <div class="text-h5 font-weight-bold">{{ formatScore(orderMatchScore) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="12" md="4">
            <v-sheet
              rounded
              class="metric-sheet pa-4"
              :style="kpiStyleByOpenQuestions(openQuestionsScore)"
            >
              <div class="caption grey--text">
                Open Questions (0-30, normalized 0-100 for blend)
              </div>
              <div class="text-h5 font-weight-bold">{{ formatOpenQuestionsDisplay }}</div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mb-6">
      <v-card-title class="text-subtitle-1">
        <v-icon left small>mdi-comment-text-multiple-outline</v-icon>
        Cultural Questions
      </v-card-title>
      <v-card-text>
        <v-simple-table v-if="culturalQuestionRows.length" class="mb-4">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">Question</th>
                <th class="text-left">Candidate answer</th>
                <th class="text-left">Client beliefs</th>
                <th class="text-left">Explainability</th>
                <th class="text-center">Match</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in culturalQuestionRows" :key="`cr-${idx}`">
                <td>{{ row.question }}</td>
                <td>{{ row.answer }}</td>
                <td>{{ row.beliefs }}</td>
                <td>{{ row.explain }}</td>
                <td class="text-center">
                  <v-avatar
                    size="36"
                    :color="getMatchCircleColor(row.matchValue)"
                    class="white--text font-weight-bold"
                  >
                    {{ row.matchDisplay }}
                  </v-avatar>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <v-card v-if="beliefComparisonRows.length" class="mb-6">
      <v-card-title class="text-subtitle-1">
        <v-icon left small>mdi-compare</v-icon>
        Beliefs Ranking
      </v-card-title>
      <v-card-text>
        <v-simple-table class="cf-beliefs-ranking-table">
          <template #default>
            <thead>
              <tr>
                <th class="text-center">Ideal order</th>
                <th class="text-left">Belief</th>
                <th class="text-center">Type</th>
                <th class="text-center">Candidate order</th>
                <th class="text-center cf-beliefs-ranking-table__points-head">Points (0-10)</th>
                <th class="text-left">Explainability</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in beliefComparisonRows"
                :key="`cmp-${idx}`"
                :class="{ 'cf-belief-distractor': !row.isClient }"
              >
                <td class="text-center">{{ row.clientRank || '--' }}</td>
                <td>{{ row.beliefName }}</td>
                <td class="text-center">
                  <v-chip
                    x-small
                    :color="row.isClient ? 'primary' : 'grey lighten-2'"
                    :text-color="row.isClient ? 'white' : 'black'"
                  >
                    {{ row.isClient ? 'Client' : 'Distractor' }}
                  </v-chip>
                </td>
                <td class="text-center">{{ row.candidateRank || '--' }}</td>
                <td class="text-center cf-beliefs-ranking-table__points-cell">
                  <v-avatar
                    v-if="row.points != null"
                    size="44"
                    :color="getMatchCircleColor(beliefPointsAsPercent(row.points))"
                    class="white--text font-weight-bold cf-beliefs-ranking-table__points-avatar"
                  >
                    {{ formatBeliefPointsDisplay(row.points) }}
                  </v-avatar>
                  <span v-else class="grey--text">--</span>
                </td>
                <td class="text-left caption" style="max-width: 320px">{{ row.reason || '--' }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div class="mt-3 caption grey--text">
          Points = order match (10 = exact rank). Open Questions (verbal affinity, 0-30):
          <strong>{{ formatRawScore(openQuestionsScore) }}</strong>.
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mb-6">
      <v-card-title class="text-subtitle-1">
        <v-icon left small>mdi-code-json</v-icon>
        Full cultural fit payload
      </v-card-title>
      <v-card-text>
        <div class="raw-json-container">
          <JsonTreeViewer
            v-if="culturalFitResult && Object.keys(culturalFitResult).length"
            :data="culturalFitResult"
          />
          <p v-else class="grey--text mb-0">No cultural fit analysis payload available.</p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import JsonTreeViewer from './JsonTreeViewer.vue';

export default {
  name: 'CulturalFitTab',
  components: { JsonTreeViewer },

  props: {
    analysisData: {
      type: Object,
      default: () => ({}),
    },
    beliefsAnalysis: {
      type: Object,
      default: () => null,
    },
  },

  computed: {
    fullInterviewResult() {
      return this.beliefsAnalysis?.fullInterviewResult || null;
    },

    culturalFitResult() {
      return this.fullInterviewResult?.results?.cultural_fit || null;
    },

    globalMatchScore() {
      return this.culturalFitResult?.match ?? null;
    },

    orderMatchScore() {
      return (
        this.culturalFitResult?.order_match?.score ??
        this.culturalFitResult?.orderMatch?.score ??
        null
      );
    },

    openQuestionsScore() {
      const raw =
        this.culturalFitResult?.beliefs?.open_questions?.match?.score ??
        this.culturalFitResult?.open_questions?.raw_score_0_30 ??
        this.culturalFitResult?.open_questions?.match?.score ??
        null;
      return raw;
    },

    openQuestionsNormalized() {
      const raw = this.openQuestionsScore;
      if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return null;
      return (Number(raw) / 30) * 100;
    },

    formatOpenQuestionsDisplay() {
      const raw = this.openQuestionsScore;
      if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return '--';
      const norm = this.openQuestionsNormalized;
      return `${Number(raw)} / 30 (${Math.round(norm)}% norm)`;
    },

    customerQuestions() {
      const snake = this.culturalFitResult?.customer_questions;
      const camel = this.culturalFitResult?.customerQuestions;
      const list = Array.isArray(snake) ? snake : Array.isArray(camel) ? camel : [];
      return list;
    },

    culturalQuestionRows() {
      const cq = this.customerQuestions;
      const reasons = (this.beliefBreakdown || []).map((x) => x.reason).filter(Boolean);
      const beliefs = Array.isArray(this.requiredBeliefs) ? this.requiredBeliefs.join(', ') : '--';
      // Blended open-questions score, used only as a fallback when a question has no
      // per-question match of its own.
      const blended =
        this.openQuestionsNormalized != null ? Math.round(this.openQuestionsNormalized) : null;
      const out = [];
      for (let i = 0; i < 2; i++) {
        const q = cq[i] || {};
        const qt = q.question_text ?? q.questionText ?? '';
        const ans = q.candidate_answer ?? q.candidateAnswer ?? '';
        // Per-question match: each question scored on its own (never blended together).
        const perQ = q.match_score ?? q.matchScore;
        const matchValue =
          perQ != null && Number.isFinite(Number(perQ)) ? Math.round(Number(perQ)) : blended;
        out.push({
          question: qt || '--',
          answer: ans || '--',
          beliefs,
          explain: reasons[i] || '--',
          matchValue,
          matchDisplay: matchValue != null ? `${matchValue}%` : '--',
        });
      }
      return out;
    },

    requiredBeliefs() {
      if (Array.isArray(this.culturalFitResult?.top3_required_beliefs)) {
        return this.culturalFitResult.top3_required_beliefs;
      }
      const fromBreakdown = (this.beliefBreakdown || [])
        .filter((item) => item?.is_client_belief)
        .map((item) => item?.belief_name)
        .filter(Boolean);
      return fromBreakdown;
    },
    beliefBreakdown() {
      const block =
        this.culturalFitResult?.beliefs?.open_questions?.scoring_breakdown?.scoring_breakdown
          ?.belief_analysis || [];
      return Array.isArray(block) ? block : [];
    },
    /** Ordered belief labels from the candidate LIST step (1-based rank = index + 1). */
    candidateBeliefOrderList() {
      const b = this.culturalFitResult?.beliefs;
      const list = b?.candidate_belief_order ?? b?.candidateBeliefOrder ?? [];
      return Array.isArray(list) ? list.map((x) => String(x ?? '').trim()).filter(Boolean) : [];
    },
    /** Normalized label -> 1-based position in candidate LIST order (first match wins). */
    candidateListRankByBeliefKey() {
      const map = {};
      this.candidateBeliefOrderList.forEach((label, idx) => {
        const key = this.normalizeBeliefKey(label);
        if (key && map[key] == null) map[key] = idx + 1;
      });
      return map;
    },
    clientBeliefsScoreByName() {
      const map = {};
      this.beliefBreakdown.forEach((item) => {
        if (item?.is_client_belief) {
          map[item?.belief_name || ''] = Number(item?.points_awarded || 0);
        }
      });
      return map;
    },
    clientBeliefsScoreSum() {
      return this.requiredBeliefs.reduce(
        (acc, belief) => acc + Number(this.clientBeliefsScoreByName?.[belief] || 0),
        0
      );
    },
    beliefComparisonRows() {
      // Preferred: the full deterministic beliefs table (every belief, client + distractor,
      // with its ideal and candidate position), built from customer_beliefs.
      const beliefs =
        this.culturalFitResult?.customer_beliefs ?? this.culturalFitResult?.customerBeliefs ?? [];
      if (Array.isArray(beliefs) && beliefs.length) {
        return beliefs
          .map((row) => {
            const name = row.label ?? row.Label ?? '';
            const isClient = row.is_client_belief ?? row.isClientBelief ?? false;
            const clientRank = row.client_order_index ?? row.clientOrderIndex ?? null;
            const candidateRank = row.candidate_order_index ?? row.candidateOrderIndex ?? null;
            // Ranking points (0-10) reflect POSITIONAL match: derived from the per-belief
            // positional score (match_score 0-100 -> 0-10), so a belief placed in its ideal
            // position scores 10. Falls back to the open-questions points if no positional
            // score is present. Client beliefs only; distractors show '--'.
            const matchScore = row.match_score ?? row.matchScore ?? null;
            const positionalPoints =
              matchScore != null && Number.isFinite(Number(matchScore))
                ? Number(matchScore) / 10
                : null;
            const points = isClient
              ? positionalPoints != null
                ? positionalPoints
                : (this.clientBeliefsScoreByName?.[name] ?? null)
              : null;
            return {
              beliefName: name,
              isClient,
              clientRank,
              candidateRank,
              points: points != null ? Number(points) : null,
              // Explainability here describes the ORDER match (coherent with the positional
              // Points column). The semantic-affinity reason is shown in Cultural Questions.
              reason: isClient ? this.orderExplainability(clientRank, candidateRank) : '',
            };
          })
          .sort((a, b) => (a.clientRank ?? 999) - (b.clientRank ?? 999));
      }
      // Legacy fallback: only the client beliefs from the open-questions breakdown.
      return this.requiredBeliefs.map((belief, idx) => {
        const score = Number(this.clientBeliefsScoreByName?.[belief] || 0);
        const candidateRank = this.resolveCandidateListRank(belief);
        return {
          beliefName: belief,
          isClient: true,
          clientRank: idx + 1,
          candidateRank,
          points: score,
          reason: this.orderExplainability(idx + 1, candidateRank),
        };
      });
    },
  },

  methods: {
    normalizeBeliefKey(s) {
      return String(s ?? '')
        .trim()
        .toLowerCase();
    },
    /**
     * Order-based explainability for the Beliefs Ranking table: it justifies the positional
     * Points column (10 = exact rank). Kept independent from the semantic-affinity reason,
     * which is shown in the Cultural Questions table. Content stays in Spanish to match the
     * rest of the analysis narrative.
     */
    orderExplainability(clientRank, candidateRank) {
      const ideal = Number(clientRank);
      const got = Number(candidateRank);
      if (!Number.isFinite(ideal) || !Number.isFinite(got)) return '--';
      const diff = Math.abs(got - ideal);
      if (diff === 0) {
        return `Coincidencia exacta de orden: el candidato la situó en la posición ${ideal}, igual que el orden ideal del cliente.`;
      }
      const plural = diff === 1 ? 'puesto' : 'puestos';
      return `El candidato la situó en la posición ${got}, frente a la posición ideal ${ideal} del cliente (desviación de ${diff} ${plural}).`;
    },
    /** 1-based rank from LIST order; fallback to customer_beliefs.candidate_order_index when present. */
    resolveCandidateListRank(beliefName) {
      const key = this.normalizeBeliefKey(beliefName);
      const fromList = this.candidateListRankByBeliefKey[key];
      if (fromList != null) return fromList;
      const beliefs =
        this.culturalFitResult?.customer_beliefs ?? this.culturalFitResult?.customerBeliefs ?? [];
      if (!Array.isArray(beliefs)) return null;
      for (let i = 0; i < beliefs.length; i++) {
        const row = beliefs[i];
        const label = row?.label ?? row?.Label ?? '';
        if (this.normalizeBeliefKey(label) !== key) continue;
        const idx = row?.candidate_order_index ?? row?.candidateOrderIndex;
        if (idx != null && Number.isFinite(Number(idx))) return Number(idx);
      }
      return null;
    },
    kpiToneColor(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '#b0bec5';
      const v = Number(value);
      if (v >= 80) return '#66bb6a';
      if (v >= 65) return '#42a5f5';
      if (v >= 45) return '#ffca28';
      return '#ef5350';
    },
    kpiStyleByPercent(percent) {
      const color = this.kpiToneColor(percent);
      return {
        backgroundColor: `${color}22`,
        border: `1px solid ${color}66`,
      };
    },
    kpiStyleByOpenQuestions(score030) {
      if (score030 === null || score030 === undefined || Number.isNaN(Number(score030))) {
        return this.kpiStyleByPercent(null);
      }
      const percent = (Number(score030) / 30) * 100;
      return this.kpiStyleByPercent(percent);
    },
    getMatchCircleColor(score) {
      if (score === null || score === undefined || Number.isNaN(Number(score))) return 'grey';
      const n = Number(score);
      if (n >= 80) return 'success';
      if (n >= 50) return 'warning';
      return 'error';
    },
    beliefPointsAsPercent(points010) {
      const n = Number(points010);
      if (Number.isNaN(n)) return null;
      return Math.min(100, Math.max(0, n * 10));
    },
    formatBeliefPointsDisplay(points010) {
      const n = Number(points010);
      if (Number.isNaN(n)) return '--';
      return String(Math.round(n * 10) / 10).replace(/\.0$/, '');
    },
    formatScore(score) {
      if (score === null || score === undefined || Number.isNaN(Number(score))) return '--';
      return `${Math.round(Number(score))}%`;
    },
    formatRawScore(score) {
      if (score === null || score === undefined || Number.isNaN(Number(score))) return '--';
      return Number(score);
    },
  },
};
</script>

<style scoped>
.cultural-fit-tab {
  padding: 16px 0;
}

.metric-sheet {
  background: #f7f9fc;
  border: 1px solid #e5eaf2;
}

.raw-json-container {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  max-height: 70vh;
}

.cf-beliefs-ranking-table >>> .cf-beliefs-ranking-table__points-head,
.cf-beliefs-ranking-table >>> .cf-beliefs-ranking-table__points-cell {
  width: 112px;
  min-width: 112px;
}

.cf-beliefs-ranking-table >>> .cf-beliefs-ranking-table__points-avatar {
  font-size: 0.95rem;
}
</style>
